/**
 * Document Section Component
 * Displays document list and actions within an expanded month row
 */
import React from 'react';
import { Button } from '../../components/ui/button';
import { 
  FileText, 
  Upload, 
  Download, 
  Eye, 
  RefreshCw, 
  Loader2,
  Trash2,
  Edit,
  Ship,
  Lock
} from 'lucide-react';

const DocumentSection = ({ 
  documents, 
  comparison,
  monthKey,
  extractingDoc,
  onUploadClick,
  onPreview,
  onDownload,
  onExtract,
  onEdit,
  onDelete,
  onExtractWithPassword,
  onSavePassword
}) => {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">Documents</p>
        <Button
          variant="outline"
          size="sm"
          onClick={onUploadClick}
          className="h-7 text-xs border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
        >
          <Upload className="h-3 w-3 mr-1" />
          Add Document
        </Button>
      </div>
      
      {documents.length > 0 ? (
        <div className="space-y-2">
          {documents.map((doc) => {
            const hasExtractedData = doc.extracted_data || (comparison?.wage_slips?.find(w => w.document_id === doc.id));
            const extractedInfo = comparison?.wage_slips?.find(w => w.document_id === doc.id);
            const isPdf = doc.file_name?.toLowerCase().endsWith('.pdf');
            
            return (
              <div key={doc.id} className="py-2 px-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-blue-400" />
                    <div>
                      <p className="text-sm font-medium text-white">{doc.file_name}</p>
                      <p className="text-xs text-slate-500">
                        {doc.document_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        {hasExtractedData && <span className="text-amber-400 ml-2">• Data extracted</span>}
                        {doc.has_saved_password && <span className="text-sky-400 ml-2 inline-flex items-center gap-0.5"><Lock className="h-2.5 w-2.5" /> Password saved</span>}
                      </p>
                      {doc.linked_trip && (
                        <p className="text-xs text-amber-400/70 flex items-center gap-1 mt-0.5">
                          <Ship className="h-3 w-3" />
                          {doc.linked_trip.vessel_name || doc.linked_trip.trip_name || 'Linked Trip'}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {/* Extract button - show for all PDFs */}
                    {isPdf && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onExtractWithPassword) {
                            onExtractWithPassword(doc);
                          } else {
                            onExtract(doc.id);
                          }
                        }}
                        disabled={extractingDoc === doc.id}
                        className={`h-8 text-xs ${hasExtractedData ? 'text-slate-400 hover:text-slate-300' : 'text-amber-400 hover:text-amber-300'}`}
                        title={hasExtractedData ? "Re-extract data (with password if needed)" : "Extract data from PDF"}
                      >
                        {extractingDoc === doc.id ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3 w-3 mr-1" />
                        )}
                        {hasExtractedData ? 'Re-extract' : 'Extract'}
                      </Button>
                    )}
                    {isPdf && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onSavePassword) onSavePassword(doc);
                        }}
                        className={`h-8 w-8 ${doc.has_saved_password ? 'text-sky-400 hover:text-sky-300' : 'text-slate-500 hover:text-amber-400'}`}
                        title={doc.has_saved_password ? 'Password saved — click to update' : 'Save PDF password'}
                        data-testid={`save-password-doc-${doc.id}`}
                      >
                        <Lock className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPreview(doc.id);
                      }}
                      className="h-8 w-8 text-slate-400 hover:text-amber-400"
                      title="Preview"
                      data-testid={`preview-doc-${doc.id}`}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDownload(doc.id, doc.file_name);
                      }}
                      className="h-8 w-8 text-slate-400 hover:text-white"
                      title="Download"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(doc);
                      }}
                      className="h-8 w-8 text-blue-400 hover:text-blue-300"
                      title="Edit document details"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(doc);
                      }}
                      className="h-8 w-8 text-red-400 hover:text-red-300"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {/* Show extracted data if available */}
                {extractedInfo && extractedInfo.gross_earnings && (
                  <div className="mt-2 pt-2 border-t border-slate-700/50 grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <span className="text-slate-500">Gross: </span>
                      <span className="text-white font-medium">£{extractedInfo.gross_earnings?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                    </div>
                    {extractedInfo.income_tax && (
                      <div>
                        <span className="text-slate-500">Tax: </span>
                        <span className="text-orange-400 font-medium">£{extractedInfo.income_tax?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    {extractedInfo.day_rate && (
                      <div>
                        <span className="text-slate-500">Day Rate: </span>
                        <span className="text-blue-400 font-medium">£{extractedInfo.day_rate?.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-slate-500 py-2">No wage slip uploaded yet</p>
      )}
    </div>
  );
};

export default DocumentSection;
